/* ==========================================================================
   Vinhuset storefront behaviour (loaded deferred after theme.js)
   - Favourites (wishlist)
   Further modules register themselves on window.vh.
   ========================================================================== */
(function () {
  'use strict';

  const vh = (window.vh = window.vh || {});
  vh.config = vh.config || {};

  /* Mobile menu opens under the header (logo, search and cart stay visible): its top follows the header's
     bottom edge. Tapping the header search closes the menu first (the menu keeps focus inside itself). */
  const headerEdge = () => document.querySelector('.theme__header') || document.querySelector('.header__mobile');
  (function mobileMenuUnderHeader() {
    const setTop = () => {
      const head = headerEdge();
      if (!head) return;
      const bottom = Math.max(0, Math.round(head.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--vh-mm-top', `${bottom}px`);
    };
    const drawer = () => document.querySelector('header-drawer.drawer--header');
    document.addEventListener('theme:drawer:open', (event) => {
      if (!event.target.matches || !event.target.matches('.drawer--header')) return;
      setTop();
      requestAnimationFrame(setTop);
    });
    window.addEventListener('resize', () => {
      const d = drawer();
      if (d && d.classList.contains('is-open')) setTop();
    }, {passive: true});
    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest || !event.target.closest('.vh-mobile-search')) return;
      const d = drawer();
      if (d && d.classList.contains('is-open')) d.dispatchEvent(new CustomEvent('theme:drawer:close', {bubbles: true}));
    });
  })();

  /* Product card quantity selector (1, 2, 3 kasser): the chosen case's price per bottle, old price, saving and
     total on the card. The selector itself is the form's variant field, so the add button adds that case. */
  document.addEventListener('change', (event) => {
    const select = event.target.closest && event.target.closest('[data-vh-card-qty]');
    if (!select) return;
    const option = select.selectedOptions && select.selectedOptions[0];
    const card = select.closest('.vh-card');
    if (!option || !card) return;
    const set = (selector, text) => {
      const el = card.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('[data-vh-card-per]', option.dataset.per || '');
    const compareWrap = card.querySelector('[data-vh-card-compare-wrap]');
    if (compareWrap) {
      compareWrap.hidden = !option.dataset.compare;
      set('[data-vh-card-compare]', option.dataset.compare || '');
    }
    const save = card.querySelector('[data-vh-card-save]');
    if (save) {
      save.hidden = !option.dataset.save;
      save.textContent = option.dataset.save || '';
    }
    set('[data-vh-card-cond]', option.dataset.cond || '');
    set('[data-vh-card-total]', option.dataset.total || '');
    const button = card.querySelector('.vh-card__btn[data-label-base]');
    if (button) button.setAttribute('aria-label', `${button.dataset.labelBase} (${option.dataset.title || ''})`);
  });

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  vh.formatMoney = function (cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  };

  // "YYYY-MM-DD HH:MM" in Danish time (Europe/Copenhagen) → Date. Date-only values end at 23:59.
  // An ISO time with a UTC offset (a metafield, "2026-10-05T23:59:00+0200") is taken as it is, in any store's time zone.
  vh.parseCphDate = function (value) {
    const text = String(value || '').trim();
    const iso = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?([+-]\d{2}):?(\d{2})$/);
    if (iso) {
      const exact = new Date(`${iso[1]}T${iso[2]}:${iso[3] || '00'}${iso[4]}:${iso[5]}`);
      if (!Number.isNaN(exact.getTime())) return exact;
    }
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    if (!match) return null;
    const [, y, m, d, hh = '23', mm = '59'] = match;
    const guess = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm));
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false}).formatToParts(new Date(guess));
      const get = (type) => Number((parts.find((p) => p.type === type) || {}).value);
      const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
      return new Date(guess - (wall - guess));
    } catch (e) {
      return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm));
    }
  };

  vh.storage = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable (private mode) — ignore */
      }
    },
  };

  /* ------------------------------------------------------------------------
     Favourites
     Legacy key "productHandleArray" keeps customers' favourites from the old theme.
     ------------------------------------------------------------------------ */
  const WISHLIST_KEY = 'productHandleArray';

  const wishlist = {
    all() {
      const list = vh.storage.get(WISHLIST_KEY, []);
      return Array.isArray(list) ? list.filter(Boolean) : [];
    },
    has(handle) {
      return this.all().includes(handle);
    },
    toggle(handle) {
      let list = this.all();
      const exists = list.includes(handle);
      list = exists ? list.filter((h) => h !== handle) : [handle, ...list];
      vh.storage.set(WISHLIST_KEY, list);
      document.dispatchEvent(new CustomEvent('vh:wishlist:change', {detail: {handle, added: !exists, list}}));
      return !exists;
    },
    // Silent removal (e.g. a product that no longer exists); only refreshes the counters.
    remove(handle) {
      vh.storage.set(WISHLIST_KEY, this.all().filter((h) => h !== handle));
      document.querySelectorAll('[data-vh-wishlist-count]').forEach((el) => {
        const count = this.all().length;
        el.textContent = count;
        el.hidden = count === 0;
      });
    },
  };
  vh.wishlist = wishlist;

  class VhWishlist extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.handle = this.dataset.handle;
      if (!this.button || !this.handle) return;
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('vh:wishlist:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('vh:wishlist:change', this.sync);
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const added = wishlist.toggle(this.handle);
      if (added) {
        this.button.classList.remove('is-popping');
        void this.button.offsetWidth;
        this.button.classList.add('is-popping');
      }
    }

    sync() {
      const active = wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  if (!customElements.get('vh-wishlist')) {
    customElements.define('vh-wishlist', VhWishlist);
  }

  /* Header / anywhere count badges: <span data-vh-wishlist-count></span> */
  function updateWishlistCounts() {
    const count = wishlist.all().length;
    document.querySelectorAll('[data-vh-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
  document.addEventListener('vh:wishlist:change', updateWishlistCounts);
  document.addEventListener('DOMContentLoaded', updateWishlistCounts);
  updateWishlistCounts();

  /* Favourites page: <vh-wishlist-grid data-url="/search?view=vh-wishlist"> */
  class VhWishlistGrid extends HTMLElement {
    connectedCallback() {
      this.onChange = (event) => {
        const detail = event.detail || {};
        const grid = this.querySelector('[data-wishlist-items]');
        if (detail.added === false && grid && grid.children.length) {
          const button = Array.from(grid.querySelectorAll('vh-wishlist')).find((el) => el.dataset.handle === detail.handle);
          const card = button && button.closest('grid-item');
          if (card) card.remove();
          if (!grid.children.length) this.render();
          return;
        }
        this.render();
      };
      document.addEventListener('vh:wishlist:change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('vh:wishlist:change', this.onChange);
    }

    async render() {
      const handles = wishlist.all().slice(0, 48);
      const empty = this.querySelector('[data-wishlist-empty]');
      const loading = this.querySelector('[data-wishlist-loading]');
      const grid = this.querySelector('[data-wishlist-items]');
      if (!grid) return;

      if (!handles.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        if (loading) loading.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      if (loading && !grid.children.length) loading.hidden = false;
      this.classList.add('is-loading');

      const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      const fetchCard = async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=vh-api-product-card`);
          if (!response.ok) return '';
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = doc.querySelector('grid-item');
          // An empty section means the product no longer exists (or is hidden): forget it.
          if (!card) wishlist.remove(handle);
          return card ? card.outerHTML : '';
        } catch (e) {
          return '';
        }
      };

      const cards = [];
      for (let i = 0; i < handles.length; i += 6) {
        cards.push(...(await Promise.all(handles.slice(i, i + 6).map(fetchCard))));
      }
      grid.innerHTML = cards.join('');
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = grid.children.length > 0;
      this.classList.remove('is-loading');
    }
  }

  if (!customElements.get('vh-wishlist-grid')) {
    customElements.define('vh-wishlist-grid', VhWishlistGrid);
  }

  /* ------------------------------------------------------------------------
     Delivery promise (PDP): cut-off countdown + expected delivery window.
     Uses the warehouse's time zone (Europe/Copenhagen); honours ship days and closed dates.
     Every word and pattern comes from the store's language file (snippets/vh-config.liquid).
     ------------------------------------------------------------------------ */
  const STRINGS = vh.config.strings || {};
  const DAY_NAMES = STRINGS.days || ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // "tirsdag d. 29/9" – weekday and date in the store's language (vinhuset.date.weekday_date)
  function weekdayDate(date) {
    return String(STRINGS.weekdayDate || '{{ weekday }} {{ day }}/{{ month }}')
      .replace('{{ weekday }}', DAY_NAMES[date.getDay()])
      .replace('{{ day }}', date.getDate())
      .replace('{{ month }}', date.getMonth() + 1);
  }

  // "2 t 49 min" – hours and minutes in the store's language (vinhuset.time.*)
  function duration(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const pattern = h > 0 ? (m > 0 ? STRINGS.hoursMinutes : STRINGS.hours) : STRINGS.minutes;
    return String(pattern || (h > 0 ? '{{ h }} h {{ m }} min' : '{{ m }} min')).replace('{{ h }}', h).replace('{{ m }}', m);
  }

  function copenhagenNow() {
    const now = new Date();
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false}).formatToParts(now);
      const get = (type) => Number((parts.find((p) => p.type === type) || {}).value);
      return new Date(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    } catch (e) {
      return now;
    }
  }

  function ymd(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  class VhDelivery {
    constructor(el) {
      this.el = el;
      this.cutoffHour = Number(el.dataset.cutoffHour || 14);
      this.cutoffMinute = Number(el.dataset.cutoffMinute || 0);
      this.shipDays = String(el.dataset.shipDays || '1,2,3,4,5').split(',').map((d) => Number(d) % 7);
      this.minDays = Number(el.dataset.minDays || 1);
      this.maxDays = Number(el.dataset.maxDays || 2);
      this.closed = String(el.dataset.closed || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.cutoffEl = el.querySelector('[data-vh-cutoff]');
      this.cutoffText = el.querySelector('[data-vh-cutoff-text]');
      this.etaEl = el.querySelector('[data-vh-eta]');
      this.etaText = el.querySelector('[data-vh-eta-text]');
      this.whenEl = el.querySelector('[data-vh-when]');
      this.whenText = el.querySelector('[data-vh-when-text]');
      this.strings = vh.config.strings || {};
      this.render();
      this.timer = setInterval(() => this.render(), 30000);
    }

    isShipDay(date) {
      return this.shipDays.includes(date.getDay()) && !this.closed.includes(ymd(date));
    }

    isDeliveryDay(date) {
      const day = date.getDay();
      return day !== 0 && day !== 6 && !this.closed.includes(ymd(date));
    }

    nextShipDate(now) {
      const date = new Date(now);
      const cutoff = new Date(now);
      cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
      if (!(this.isShipDay(date) && now < cutoff)) {
        do {
          date.setDate(date.getDate() + 1);
        } while (!this.isShipDay(date));
      }
      return date;
    }

    addDeliveryDays(date, days) {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (this.isDeliveryDay(d)) added++;
      }
      return d;
    }

    formatDay(date, now) {
      const today = ymd(now);
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (ymd(date) === today) return this.strings.today || 'today';
      if (ymd(date) === ymd(tomorrow)) return this.strings.tomorrow || 'tomorrow';
      return weekdayDate(date);
    }

    render() {
      const now = copenhagenNow();
      const ship = this.nextShipDate(now);
      const shipsToday = ymd(ship) === ymd(now);

      if (this.cutoffEl && this.cutoffText) {
        if (shipsToday) {
          const cutoff = new Date(now);
          cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
          const mins = Math.max(0, Math.round((cutoff - now) / 60000));
          const time = duration(mins);
          this.cutoffText.innerHTML = (this.strings.cutoffToday || 'Order within <strong>{{ time }}</strong> and we ship today').replace('{{ time }}', time);
        } else {
          this.cutoffText.innerHTML = (this.strings.cutoffNext || 'Order now – ships <strong>{{ day }}</strong>').replace('{{ day }}', this.formatDay(ship, now));
        }
        this.cutoffEl.hidden = false;
      }

      // One definite line: the countdown and the delivery day with weekday and date,
      // e.g. "Bestil inden for 2 t 49 min – så har du den i morgen, tirsdag d. 29/9" (next-day delivery on weekdays)
      if (this.whenEl && this.whenText) {
        const arrive = this.addDeliveryDays(ship, this.minDays);
        const dated = weekdayDate(arrive);
        const relative = this.formatDay(arrive, now);
        const range = relative === dated ? dated : `${relative}, ${dated}`;
        if (shipsToday) {
          const cutoff = new Date(now);
          cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
          const mins = Math.max(0, Math.round((cutoff - now) / 60000));
          const time = duration(mins);
          this.whenText.innerHTML = (this.strings.whenToday || 'Order within <strong>{{ time }}</strong> – delivered <strong>{{ day }}</strong>').replace('{{ time }}', time).replace('{{ day }}', range).replace('{{ range }}', range);
        } else {
          this.whenText.innerHTML = (this.strings.whenNext || 'Order now – delivered <strong>{{ day }}</strong>').replace('{{ day }}', range).replace('{{ range }}', range);
        }
        this.whenEl.hidden = false;
      }

      if (this.etaEl && this.etaText) {
        const from = this.addDeliveryDays(ship, this.minDays);
        const to = this.addDeliveryDays(ship, this.maxDays);
        const fromText = this.formatDay(from, now);
        const toText = this.formatDay(to, now);
        const range = ymd(from) === ymd(to) ? fromText : `${fromText} – ${toText}`;
        this.etaText.innerHTML = (this.strings.eta || 'Forventet levering <strong>{{ range }}</strong>').replace('{{ range }}', range);
        this.etaEl.hidden = false;
      }
    }
  }

  function initDelivery(root = document) {
    root.querySelectorAll('[data-vh-delivery]').forEach((el) => {
      if (el._vhDelivery) return;
      el._vhDelivery = new VhDelivery(el);
    });
  }
  document.addEventListener('DOMContentLoaded', () => initDelivery());
  document.addEventListener('shopify:section:load', (e) => initDelivery(e.target));
  initDelivery();

  /* PDP intro: show "Læs mere" only when the clamped text is actually cut */
  function markClamped() {
    document.querySelectorAll('[data-vh-clamp]').forEach((text) => {
      const box = text.parentElement;
      if (box && !box.classList.contains('is-open')) box.classList.toggle('is-clamped', text.scrollHeight > text.clientHeight + 1);
    });
  }
  // The summary's "Læs mere" opens the rest of the summary in place (see snippets/vh-product-block.liquid)
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-vh-intro-open]');
    if (!more) return;
    const box = more.closest('.vh-pb-intro');
    if (!box) return;
    box.classList.remove('is-clamped');
    box.classList.add('is-open');
    more.setAttribute('aria-expanded', 'true');
  });
  markClamped();
  let clampTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(clampTimer);
    clampTimer = setTimeout(markClamped, 150);
  });
  document.addEventListener('shopify:section:load', markClamped);

  /* One debounced /cart.js fetch per cart change, shared by everything that follows the live cart */
  const cartListeners = [];
  let cartTimer = null;
  vh.onCartChange = function (callback) {
    cartListeners.push(callback);
  };
  document.addEventListener('theme:cart:change', () => {
    // The cart Shopify rendered with this change, when assets/vh-cart.js has it (saves the request)
    const known = typeof vh.renderedCart === 'function' ? vh.renderedCart() : null;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(async () => {
      if (!cartListeners.length) return;
      try {
        const cart = known || (await (await fetch(`${window.theme.routes.cart_url}.js`)).json());
        cartListeners.forEach((callback) => callback(cart));
      } catch (e) {}
    }, 200);
  });

  /* Free-shipping hint on the PDP follows the live cart */
  vh.onCartChange((cart) => {
    document.querySelectorAll('[data-vh-freeship]').forEach((hint) => {
      const limit = Number(hint.dataset.limit || 0);
      const text = hint.querySelector('[data-vh-freeship-text]');
      if (!text || !limit) return;
      const left = limit - Number(cart.total_price || 0);
      text.textContent = left > 0 ? (vh.config.strings.freeLeft || '{{ amount }} to free shipping').replace('{{ amount }}', vh.formatMoney(left)) : vh.config.strings.freeOk || 'Free shipping';
    });
  });

  /* Header (desktop): cart total + free-shipping / tier progress, as on the live site */
  vh.onCartChange((cart) => {
    document.querySelectorAll('[data-vh-hcart]').forEach((el) => {
      const amounts = String(el.dataset.amounts || '').split('|').map(Number).filter((n) => n > 0);
      const labels = String(el.dataset.labels || '').split('¦');
      const total = Number(cart.total_price || 0);
      const max = amounts[amounts.length - 1] || 0;
      const next = amounts.findIndex((amount) => total < amount);
      let message = el.dataset.strDone || '';
      if (next >= 0) {
        message = (el.dataset.strLeft || '').replace('[amount]', vh.formatMoney(amounts[next] - total)).replace('[label]', labels[next] || '');
      } else if (amounts.length > 1) {
        message = (el.dataset.strAll || '').replace('[label]', labels[labels.length - 1] || '');
      }
      const percent = max > 0 ? Math.min(100, Math.floor((total / max) * 100)) : 0;
      const totalEl = el.querySelector('[data-vh-hcart-total]');
      const messageEl = el.querySelector('[data-vh-hcart-msg]');
      const fill = el.querySelector('[data-vh-hcart-fill]');
      if (totalEl) totalEl.textContent = vh.formatMoney(total);
      if (messageEl) messageEl.innerHTML = message;
      if (fill) fill.style.width = `${percent}%`;
      el.classList.toggle('is-complete', next < 0);
    });
  });

  /* Toolbar: "Bestil inden for 2t 14m 05s og vi sender din ordre i dag" (ship days only, hidden after the cut-off).
     The clock pattern is vinhuset.time.clock in the store's language. */
  function cutoffLeft(el, now) {
    const shipDays = String(el.dataset.shipDays || '1,2,3,4,5').split(',').map((d) => Number(d) % 7);
    const closed = String(el.dataset.closed || '').split(',').map((d) => d.trim()).filter(Boolean);
    const cutoff = new Date(now);
    cutoff.setHours(Number(el.dataset.cutoffHour || 14), Number(el.dataset.cutoffMinute || 0), 0, 0);
    if (!shipDays.includes(now.getDay()) || closed.includes(ymd(now)) || now >= cutoff) return 0;
    return Math.floor((cutoff - now) / 1000);
  }

  function tickCutoffBars() {
    const bars = document.querySelectorAll('[data-vh-cutoff-bar]');
    if (!bars.length) return false;
    const now = copenhagenNow();
    bars.forEach((el) => {
      const seconds = cutoffLeft(el, now);
      if (seconds <= 0) {
        el.hidden = true;
        return;
      }
      const h = Math.floor(seconds / 3600);
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
      const sec = String(seconds % 60).padStart(2, '0');
      const text = el.querySelector('[data-vh-cutoff-bar-text]');
      const clock = String(STRINGS.clock || '{{ h }}h {{ m }}m {{ s }}s').replace('{{ h }}', h).replace('{{ m }}', m).replace('{{ s }}', sec);
      if (text) text.innerHTML = String(el.dataset.template || '').replace('[time]', clock);
      el.hidden = false;
    });
    return true;
  }
  if (tickCutoffBars()) setInterval(tickCutoffBars, 1000);

  /* Toolbar on mobile: one message at a time, skipping hidden ones (e.g. the countdown after 14:00) */
  function initRotators(root = document) {
    root.querySelectorAll('[data-vh-rotator]').forEach((rotator) => {
      if (rotator._vhRotator) return;
      rotator._vhRotator = true;
      const slides = () => [...rotator.children].filter((slide) => !slide.hidden);
      let index = 0;
      const show = () => {
        const visible = slides();
        if (!visible.length) return;
        index %= visible.length;
        [...rotator.children].forEach((slide) => slide.classList.toggle('is-active', slide === visible[index]));
      };
      // The first message is shown at once; only later swaps fade
      rotator.classList.add('is-static');
      show();
      requestAnimationFrame(() => requestAnimationFrame(() => rotator.classList.remove('is-static')));
      setInterval(() => {
        if (document.hidden) return;
        index += 1;
        show();
      }, 4000);
    });
  }
  initRotators();
  document.addEventListener('shopify:section:load', (e) => initRotators(e.target));

  /* ------------------------------------------------------------------------
     <vh-buy-float>: compact buy card fixed to the bottom of the screen, as wide
     as the right column (desktop only). Visible while the real "Tilføj til
     kurv" is below the fold; hidden once it is in view – after it has been
     passed, the full-width cart bar takes over. Mirrors price, option and
     sold-out state of the buy box and submits the main product form.
     ------------------------------------------------------------------------ */
  class VhBuyFloat extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.section = this.closest('product-info') || document;
      this.form = this.section.querySelector('[data-product-form]');
      this.mainBtn = (this.form && this.form.querySelector('[data-add-to-cart]')) || this.section.querySelector('[data-add-to-cart]');
      this.column = this.closest('.product__content');
      this.btn = this.querySelector('[data-vh-float-add]');
      if (!this.form || !this.mainBtn || !this.column || !this.btn || !('IntersectionObserver' in window)) return;

      this.mq = window.matchMedia('(min-width: 990px)');
      this.below = false;
      // Observer for normal scrolling + a scroll check, since the observer stays silent when the page jumps
      // straight from below the button to above it (Home key, "back to top")
      new IntersectionObserver(() => this.check()).observe(this.mainBtn);
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.check();
        });
      };
      window.addEventListener('scroll', this.onScroll, {passive: true});

      this.onResize = () => {
        if (!this.hidden) this.place();
      };
      window.addEventListener('resize', this.onResize, {passive: true});
      if (this.mq.addEventListener) this.mq.addEventListener('change', () => this.update());

      this.btn.addEventListener('click', (event) => this.add(event));
      document.addEventListener('theme:cart:change', () => this.done());
      document.addEventListener('theme:product:add-error', () => this.done());

      const sync = () => this.sync();
      const priceBlock = this.section.querySelector('.vh-pb--vh-price');
      [priceBlock, this.form].filter(Boolean).forEach((target) => {
        new MutationObserver(sync).observe(target, {childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled']});
      });
      this.sync();
    }

    check() {
      const below = this.mainBtn.getBoundingClientRect().top > window.innerHeight;
      if (below !== this.below || this.hidden === below) {
        this.below = below;
        this.update();
      }
    }

    // As wide as the info content itself (the column has padding towards the gallery)
    place() {
      const ref = this.column.querySelector('.product__block') || this.column;
      const rect = ref.getBoundingClientRect();
      this.style.left = `${rect.left}px`;
      this.style.width = `${rect.width}px`;
    }

    update() {
      const show = this.mq.matches && this.below;
      if (show) this.place();
      this.hidden = !show;
    }

    sync() {
      const row = this.section.querySelector('.vh-pdp-price__row');
      const price = this.querySelector('[data-vh-float-price]');
      if (row && price && price.innerHTML !== row.innerHTML) price.innerHTML = row.innerHTML;
      const value = this.section.querySelector('.vh-tiles__legend-value');
      const variant = this.querySelector('[data-vh-float-variant]');
      if (value && variant && variant.textContent !== value.textContent.trim()) variant.textContent = value.textContent.trim();
      this.btn.disabled = this.mainBtn.disabled;
      const label = this.mainBtn.querySelector('[data-add-to-cart-text]');
      const text = label ? label.textContent.trim() : '';
      const own = this.btn.querySelector('.btn__text');
      if (text && own && own.textContent !== text) own.textContent = text;
    }

    add(event) {
      event.preventDefault();
      if (this.btn.disabled || this.btn.classList.contains('is-loading')) return;
      this.btn.classList.add('is-loading');
      const productForm = this.form.closest('product-form');
      if (productForm && typeof productForm.onSubmitHandler === 'function') productForm.onSubmitHandler(event);
      else this.mainBtn.click();
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => this.done(), 8000);
    }

    done() {
      this.btn.classList.remove('is-loading');
    }
  }
  if (!customElements.get('vh-buy-float')) customElements.define('vh-buy-float', VhBuyFloat);

  /* ------------------------------------------------------------------------
     <vh-cart-sticky>: /cart on a phone (sections/cart.liquid). The subtotal,
     the way to free shipping and "Til betaling" at the bottom of the screen
     while the page's own "Til betaling" is out of view (docs/QA.md, F-084).
     The total and the message update with the header's (data-vh-hcart). The
     button presses the page's own button, so the terms box, the MitID check
     and everything else on the way to checkout work as they do there.
     ------------------------------------------------------------------------ */
  class VhCartSticky extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.page = this.closest('[data-cart-page]');
      this.mainBtn = this.page && this.page.querySelector('.cart__checkout');
      this.btn = this.querySelector('[data-vh-cart-sticky-checkout]');
      if (!this.page || !this.mainBtn || !this.btn || !('IntersectionObserver' in window)) return;
      this.inView = true;
      new IntersectionObserver((entries) => {
        this.inView = entries[entries.length - 1].isIntersecting;
        this.update();
      }).observe(this.mainBtn);
      // the cart page turns "is-empty" when the last line is removed
      new MutationObserver(() => this.update()).observe(this.page, {attributes: true, attributeFilter: ['class']});
      this.btn.addEventListener('click', () => this.checkout());
    }

    update() {
      this.classList.toggle('is-visible', !this.inView && !this.page.classList.contains('is-empty'));
    }

    checkout() {
      // terms to accept first (a store setting): take the customer to them instead
      const terms = this.page.querySelector('[data-cart-acceptance-checkbox]');
      if (terms && !terms.checked) {
        terms.scrollIntoView({block: 'center', behavior: 'smooth'});
        terms.focus({preventScroll: true});
        return;
      }
      this.mainBtn.click();
    }
  }
  if (!customElements.get('vh-cart-sticky')) customElements.define('vh-cart-sticky', VhCartSticky);

  /* ------------------------------------------------------------------------
     Header search extras
     - Empty field: a full-width panel with popular searches (chips) and popular
       products (sections/vh-search-popular). It is fetched once the page has loaded
       and the browser is idle (or earlier, when the pointer or a finger reaches the
       field) and kept for 10 minutes (sessionStorage), so the first tap opens it at
       once. The panel only opens with its content in place (no empty strip first).
       A chip fills the field and shows the live results.
     - Typed results and the popular panel both sit full width right under the
       header – below the menu bar on desktop (--vh-sp-top follows the sticky header).
     - The placeholder types the popular search terms until the field is used.
     ------------------------------------------------------------------------ */
  let popularHtml = null;
  let popularRequest = null;
  const POPULAR_TTL = 10 * 60 * 1000;

  function loadPopular() {
    if (popularHtml) return Promise.resolve(popularHtml);
    if (popularRequest) return popularRequest;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cacheKey = `vh:search-popular:${root}`;
    const useCache = !(window.Shopify && window.Shopify.designMode);
    if (useCache) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && cached.html && Date.now() - cached.t < POPULAR_TTL) {
          popularHtml = cached.html;
          return Promise.resolve(popularHtml);
        }
      } catch (e) {}
    }
    popularRequest = fetch(`${root}?section_id=vh-search-popular`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.vh-sp__inner');
        popularHtml = inner ? inner.outerHTML : '';
        if (useCache && popularHtml) {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({t: Date.now(), html: popularHtml}));
          } catch (e) {}
        }
        return popularHtml;
      })
      .catch(() => {
        popularRequest = null;
        return '';
      });
    return popularRequest;
  }

  function typewriter(input) {
    const terms = String(input.dataset.vhTypewriter || '').split('|').map((t) => t.trim()).filter(Boolean);
    if (!terms.length || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    const base = input.getAttribute('placeholder') || '';
    let word = 0;
    let chars = 0;
    let deleting = false;
    let running = false;
    let timer = null;
    const step = () => {
      if (!running) return;
      // Only the visible field (desktop or mobile) animates
      if (input.offsetParent === null) {
        timer = setTimeout(step, 1200);
        return;
      }
      const text = terms[word];
      if (!deleting) {
        chars += 1;
        input.placeholder = text.slice(0, chars);
        if (chars >= text.length) {
          deleting = true;
          timer = setTimeout(step, 1700);
          return;
        }
        timer = setTimeout(step, 90);
      } else {
        chars -= 1;
        input.placeholder = text.slice(0, Math.max(chars, 0));
        if (chars <= 0) {
          deleting = false;
          word = (word + 1) % terms.length;
          timer = setTimeout(step, 450);
          return;
        }
        timer = setTimeout(step, 45);
      }
    };
    const start = (delay) => {
      if (running || input.value || document.activeElement === input) return;
      running = true;
      chars = 0;
      deleting = false;
      timer = setTimeout(step, delay);
    };
    const stop = () => {
      running = false;
      clearTimeout(timer);
      input.placeholder = base;
    };
    input.addEventListener('focus', stop);
    input.addEventListener('blur', () => {
      if (!input.value) start(2000);
    });
    // The full placeholder is shown first, so the field reads as a search field before it starts typing
    start(3000);
  }

  function initSearchExtras(root = document) {
    root.querySelectorAll('.vh-hsearch').forEach((wrap) => {
      if (wrap._vhSearch) return;
      wrap._vhSearch = true;
      const input = wrap.querySelector('.vh-hsearch__input');
      const panel = wrap.querySelector('[data-vh-search-popular]');
      if (!input) return;
      typewriter(input);

      // The panels open right under the red bar: over the menu line on desktop (the red runs on 14px below the
      // search row, so the panel starts where the menu line does), under the red header on mobile
      const edgeY = () => {
        const upper = input.closest('.vh-header__upper');
        const menu = upper && upper.parentElement.querySelector('.vh-header__lower');
        if (menu && menu.offsetHeight) return menu.getBoundingClientRect().top;
        return (upper || headerEdge() || wrap).getBoundingClientRect().bottom;
      };
      const setTop = () => {
        const bottom = Math.max(0, Math.round(edgeY()));
        document.documentElement.style.setProperty('--vh-sp-top', `${bottom}px`);
      };
      const resultsOpen = () => !!wrap.querySelector('predictive-search[open], predictive-search[loading]');
      const isOpen = () => (panel && !panel.hidden) || resultsOpen();

      const wanted = () => !input.value.trim() && wrap.contains(document.activeElement);
      const reveal = () => {
        setTop();
        panel.hidden = false;
        wrap.classList.add('is-popular-open');
      };
      // Opens only with its content in place – an empty panel would flash as a thin strip first
      const show = () => {
        if (!panel || input.value.trim()) return;
        if (panel.childElementCount) {
          reveal();
          return;
        }
        loadPopular().then((html) => {
          if (!html) return;
          if (!panel.childElementCount) {
            panel.innerHTML = html;
            if (typeof vh.updateWishlistButtons === 'function') vh.updateWishlistButtons(panel);
          }
          if (wanted()) reveal();
        });
      };
      // Fetch on intent, so the panel is ready by the time the field has focus
      const warm = () => loadPopular();
      wrap.addEventListener('pointerenter', warm, {once: true});
      wrap.addEventListener('touchstart', warm, {once: true, passive: true});
      const hide = () => {
        if (!panel) return;
        panel.hidden = true;
        wrap.classList.remove('is-popular-open');
      };

      input.addEventListener('focus', show);
      input.addEventListener('input', () => (input.value.trim() ? hide() : show()));
      wrap.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!wrap.contains(document.activeElement)) hide();
        });
      });
      // A click outside – also on the dimmed page under the panels – closes the popular panel and the typed results
      document.addEventListener('click', (event) => {
        if (wrap.contains(event.target)) return;
        hide();
        const ps = wrap.querySelector('predictive-search');
        if (ps && ps.hasAttribute('open') && typeof ps.closeResults === 'function') ps.closeResults();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') hide();
      });

      if (panel) {
        panel.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-vh-search-term]');
          if (!chip || !wrap.querySelector('predictive-search')) return;
          event.preventDefault();
          input.value = chip.dataset.vhSearchTerm;
          input.focus();
          input.dispatchEvent(new Event('input', {bubbles: true}));
        });
      }

      // Typed results open under the same row
      new MutationObserver(() => {
        if (resultsOpen()) setTop();
      }).observe(wrap, {attributes: true, subtree: true, attributeFilter: ['open', 'loading']});

      let ticking = false;
      const follow = () => {
        if (ticking || !isOpen()) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          setTop();
        });
      };
      window.addEventListener('scroll', follow, {passive: true});
      window.addEventListener('resize', follow, {passive: true});
    });
  }
  initSearchExtras();
  document.addEventListener('shopify:section:load', (e) => initSearchExtras(e.target));
  if (document.querySelector('.vh-hsearch [data-vh-search-popular]')) {
    const warmPopular = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(() => loadPopular(), {timeout: 2000});
      else setTimeout(loadPopular, 1000);
    };
    if (document.readyState === 'complete') warmPopular();
    else window.addEventListener('load', warmPopular, {once: true});
  }

  /* "Lovlig levering" (product page): the explanation opens and closes, and the store's dataLayer event follows
     ({event, state: 'open' | 'closed'}, as SE's GTM expects from the card that won its A/B test) */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-vh-legal-toggle]');
    if (!button) return;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (panel) panel.hidden = !open;
    if (button.dataset.event) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({event: button.dataset.event, state: open ? 'open' : 'closed'});
      } catch (e) {}
    }
  });

  /* PDP image badge: the discount follows the price row when another variant is chosen */
  function syncPdpDiscount() {
    document.querySelectorAll('[data-vh-pdp-discount]').forEach((pill) => {
      if (pill._vhSync) return;
      pill._vhSync = true;
      const block = document.getElementById(pill.dataset.vhPdpDiscount)?.closest('.product__block') || document.querySelector('.vh-pb--vh-price');
      if (!block) return;
      const sync = () => {
        const src = block.querySelector('.vh-pdp-price .vh-flag--sale');
        pill.hidden = !src;
        if (src) pill.textContent = src.textContent.trim();
      };
      new MutationObserver(sync).observe(block, {childList: true, subtree: true, characterData: true});
    });
  }
  syncPdpDiscount();
  document.addEventListener('shopify:section:load', syncPdpDiscount);

  /* A sold-out variant (F-141): products in stock from Shopify's recommendations under the "Udsolgt" button
     (sections/vh-soldout-alternatives.liquid), the same source as "Lignende vine" further down. The flag sits in the
     stock line, which the theme redraws for every variant, so a sold-out case chosen later fetches them too; CSS hides
     the lines that only apply to a product you can buy (vh-product.css). Fetched once per page. */
  function initSoldout(root = document) {
    root.querySelectorAll('[data-vh-soldout]').forEach((box) => {
      if (box._vhSoldout) return;
      box._vhSoldout = true;
      const info = box.closest('product-info') || document;
      let started = false;
      const load = () => {
        if (started || !info.querySelector('[data-vh-soldout-flag]')) return;
        started = true;
        fetch(box.dataset.url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const items = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vh-soldout-items]');
            const target = box.querySelector('[data-vh-soldout-items]');
            if (!items || !target || !items.children.length) return;
            target.replaceChildren(...items.children);
            box.hidden = false;
          })
          .catch(() => {});
      };
      load();
      const stock = info.querySelector('[id^="Inventory-"]');
      if (stock) new MutationObserver(load).observe(stock, {childList: true, subtree: true});
    });
  }
  initSoldout();
  document.addEventListener('shopify:section:load', (event) => initSoldout(event.target));

  /* Taste chips with the profile link: as many notes as fit on one line, then "Se vinprofil ↓" */
  function fitChips(root = document) {
    root.querySelectorAll('[data-vh-chip-fit]').forEach((list) => {
      const more = list.querySelector('.vh-pb-taste__more');
      const notes = Array.from(list.children).filter((li) => li !== more);
      if (!notes.length) return;
      notes.forEach((li) => {
        li.hidden = false;
      });
      // measured at full width (the first chip may shrink only once the others are gone; F-119)
      list.classList.add('is-measuring');
      const edge = () => list.getBoundingClientRect().right + 0.5;
      const last = () => (more || notes.filter((li) => !li.hidden).pop()).getBoundingClientRect().right;
      for (let i = notes.length - 1; i > 0 && last() > edge(); i--) notes[i].hidden = true;
      list.classList.remove('is-measuring');
    });
  }
  fitChips();
  document.fonts?.ready.then(() => fitChips());
  let fitTimer;
  window.addEventListener('resize', () => {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(() => fitChips(), 150);
  });
  document.addEventListener('shopify:section:load', (e) => fitChips(e.target));

  /* In-page scrolling ("Se vinprofil ↓", "Læs mere"): the target lands right under the sticky header. The first scroll
     aims for the header's stuck height (the top bars scroll away on the way down – Broadcast used the height at the
     click); once the page has come to rest, one correction takes up anything that moved meanwhile (lazy images, app
     widgets). No correction once the shopper scrolls themselves. */
  function vhScrollTo(target, gap = 0) {
    const header = document.querySelector('[data-header-sticky]') ? document.querySelector('[data-header-height]') : null;
    const toolbar = header && header.querySelector('.toolbar');
    const stuck = header ? header.offsetHeight - (toolbar ? toolbar.offsetHeight : 0) : 0;
    window.scrollTo({top: window.scrollY + target.getBoundingClientRect().top - stuck - gap, behavior: 'smooth'});

    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, cancel, {once: true, passive: true}));
    let last = -1;
    let still = 0;
    let frames = 0;
    const settle = () => {
      if (cancelled) return;
      frames += 1;
      still = Math.abs(window.scrollY - last) < 1 ? still + 1 : 0;
      last = window.scrollY;
      if (still < 6 && frames < 240) {
        requestAnimationFrame(settle);
        return;
      }
      const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const off = target.getBoundingClientRect().top - bottom - gap;
      if (Math.abs(off) > 4) window.scrollTo({top: window.scrollY + off, behavior: 'smooth'});
    };
    requestAnimationFrame(settle);
  }

  document.addEventListener('click', (event) => {
    const next = event.target.closest('[data-vh-scroll-next]');
    if (!next) return;
    const target = next.closest('.shopify-section')?.nextElementSibling;
    if (!target) return;
    event.preventDefault();
    vhScrollTo(target);
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-vh-scroll]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    vhScrollTo(target);
  });

  // Mobile filter drawer: "Vis X produkter" closes the drawer (filters already applied via AJAX).
  document.addEventListener('click', (event) => {
    const done = event.target.closest('[data-vh-filters-done]');
    if (!done) return;
    event.preventDefault();
    done.closest('[data-section-id]')?.dispatchEvent(new CustomEvent('theme:filter:close', {bubbles: false}));
  });

  // Compact sticky header: the utility bar slides away once the header is stuck (see vh-base.css).
  function setToolbarHeight() {
    const bar = document.querySelector('[data-header-height] .toolbar');
    const height = bar && bar.getClientRects().length ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--vh-toolbar-h', `${height}px`);
  }
  // The bar's height also changes without a resize (a message that wraps, the countdown ending), so it is observed.
  function watchToolbarHeight() {
    setToolbarHeight();
    const bar = document.querySelector('[data-header-height] .toolbar');
    if (!bar || bar._vhHeightWatch || !('ResizeObserver' in window)) return;
    bar._vhHeightWatch = new ResizeObserver(setToolbarHeight);
    bar._vhHeightWatch.observe(bar);
  }
  watchToolbarHeight();
  document.addEventListener('DOMContentLoaded', watchToolbarHeight);
  document.addEventListener('theme:resize', setToolbarHeight);
  document.addEventListener('shopify:section:load', watchToolbarHeight);

  function initExpanders(root = document) {
    root.querySelectorAll('[data-vh-expand]').forEach((wrap) => {
      if (wrap._vhExpand) return;
      wrap._vhExpand = true;
      const body = wrap.querySelector('[data-vh-expand-body]');
      const toggle = wrap.querySelector('[data-vh-expand-toggle]');
      if (!body || !toggle) return;
      if (body.scrollHeight > 420) {
        wrap.classList.add('is-collapsible');
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          const open = wrap.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          toggle.querySelector('[data-label-more]').hidden = open;
          toggle.querySelector('[data-label-less]').hidden = !open;
        });
      }
    });
  }
  document.addEventListener('DOMContentLoaded', () => initExpanders());
  document.addEventListener('shopify:section:load', (e) => initExpanders(e.target));

  // PDP gallery on phones: the dots (or "2 / 12") follow the swipe, and a dot scrolls to its image
  function initGalleryDots(root = document) {
    root.querySelectorAll('[data-vh-gallery-dots]').forEach((dots) => {
      if (dots._vhDots) return;
      dots._vhDots = true;
      const track = dots.parentElement.querySelector('[data-product-media-list]');
      if (!track) return;
      const slides = () => Array.from(track.children).filter((el) => el.classList.contains('product__slide'));
      const buttons = Array.from(dots.querySelectorAll('.vh-gdots__dot'));
      const current = dots.querySelector('[data-vh-gallery-current]');
      const step = (list) => (list.length > 1 ? list[1].offsetLeft - list[0].offsetLeft : list[0].offsetWidth) || 1;
      let ticking = false;
      const update = () => {
        ticking = false;
        const list = slides();
        if (!list.length) return;
        const index = Math.max(0, Math.min(list.length - 1, Math.round(track.scrollLeft / step(list))));
        buttons.forEach((button, i) => {
          button.classList.toggle('is-active', i === index);
          if (i === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        if (current) current.textContent = index + 1;
      };
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      }, {passive: true});
      buttons.forEach((button, i) => {
        button.addEventListener('click', () => {
          const list = slides();
          if (list[i]) track.scrollTo({left: list[i].offsetLeft - list[0].offsetLeft, behavior: 'smooth'});
        });
      });
      update();
    });
  }
  document.addEventListener('DOMContentLoaded', () => initGalleryDots());
  document.addEventListener('shopify:section:load', (e) => initGalleryDots(e.target));

  // PDP on phones: the sticky "Tilføj til kurv" bar is there from the start. It shows while the real button is still
  // below the screen and steps aside while the real button is in view; once that has been passed, Broadcast's own bar
  // logic (.is-visible) takes over. On every screen it also shows while the real button sits under the sticky header.
  // The bar and the button are looked up on every check (a variant change re-renders them).
  function initEarlyCartBar() {
    if (!document.querySelector('cart-bar.cart-bar')) return;
    const mq = window.matchMedia('(max-width: 749px)');
    let first = true;
    let ticking = false;
    const check = () => {
      ticking = false;
      const bar = document.querySelector('cart-bar.cart-bar');
      if (!bar) return;
      const section = bar.closest('product-info') || document;
      const form = section.querySelector('[data-product-form]');
      const button = (form && form.querySelector('[data-add-to-cart]')) || section.querySelector('[data-add-to-cart]');
      const rect = button ? button.getBoundingClientRect() : null;
      const early = mq.matches && !!rect && rect.top > window.innerHeight;
      // Under the sticky header the real button can't be tapped, but Broadcast's bar only comes once the button has
      // left the screen: show the bar in between too (phone and computer)
      const heads = [...document.querySelectorAll('[data-header-height], .header__wrapper')].map((h) => h.getBoundingClientRect()).filter((r) => r.height > 0 && r.top <= 1);
      const headerBottom = heads.length ? Math.max(...heads.map((r) => r.bottom)) : 0;
      bar.classList.toggle('vh-cart-bar--covered', !!rect && rect.bottom > 0 && rect.top < headerBottom);
      if (first) {
        // Already there on load – no slide-in
        first = false;
        bar.classList.add('vh-cart-bar--instant');
        bar.classList.toggle('vh-cart-bar--early', early);
        requestAnimationFrame(() => requestAnimationFrame(() => bar.classList.remove('vh-cart-bar--instant')));
        return;
      }
      bar.classList.toggle('vh-cart-bar--early', early);
    };
    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    };
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule, {passive: true});
    const info = document.querySelector('product-info');
    if (info) new MutationObserver(schedule).observe(info, {childList: true, subtree: true});
    check();
  }
  document.addEventListener('DOMContentLoaded', initEarlyCartBar);

  /* "Cookie-indstillinger" in the footer: the visitor changes their consent there, as Cookiebot's round icon is not
     shown (docs/QA.md, F-083). It opens Cookiebot's dialog again, or Shopify's own cookie banner in a store without
     Cookiebot. The button shows once one of them is on the page, so it never leads nowhere. */
  function initCookieSettings() {
    const items = document.querySelectorAll('[data-vh-cookie-settings]');
    if (!items.length) return;
    const opener = () => {
      if (window.Cookiebot && typeof window.Cookiebot.renew === 'function') return () => window.Cookiebot.renew();
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') return () => window.privacyBanner.showPreferences();
      return null;
    };
    const reveal = () => {
      if (!opener()) return false;
      // Shopify's banner adds its own "Cookie preferences" link to the policies (#shopifyReshowConsentBanner); then the
      // theme's button would be the same link twice (NL-10). Cookiebot stores keep the theme's button
      if (!window.Cookiebot && document.querySelector('a[href="#shopifyReshowConsentBanner"]')) return true;
      items.forEach((item) => {
        item.hidden = false;
      });
      return true;
    };
    items.forEach((item) => {
      item.querySelector('button').addEventListener('click', () => {
        const open = opener();
        if (open) open();
      });
    });
    if (reveal()) return;
    // Shopify's banner comes with its own script, which can arrive after this one
    let tries = 0;
    const timer = setInterval(() => {
      if (reveal() || ++tries >= 20) clearInterval(timer);
    }, 500);
  }
  document.addEventListener('DOMContentLoaded', initCookieSettings);

  // Footer menus collapse into accordions on mobile.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-vh-footer-toggle]');
    if (!toggle || window.matchMedia('(min-width: 750px)').matches) return;
    const col = toggle.closest('[data-vh-footer-col]');
    const open = col.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     <vh-slider>: native scroll-snap slider.
     data-mode="slide" (one slide per view, dots + optional autoplay) or
     data-mode="page" (product rails: arrows scroll one view at a time).
     ------------------------------------------------------------------------ */
  class VhSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-vh-slider-track]');
      if (!this.track) return;
      this.mode = this.dataset.mode || 'slide';
      this.prevBtn = this.querySelector('[data-vh-slider-prev]');
      this.nextBtn = this.querySelector('[data-vh-slider-next]');
      this.slides = Array.from(this.track.children);
      this.index = 0;

      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.sync();
        });
      }, {passive: true});

      if (this.mode === 'slide') this.buildDots();
      this.sync();
      if (this.mode === 'slide') this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stop();
      this.visibilityObserver?.disconnect();
    }

    buildDots() {
      const wrap = this.querySelector('[data-vh-slider-dots]');
      if (!wrap || this.slides.length < 2) return;
      const label = wrap.dataset.label || 'Slide';
      wrap.innerHTML = '';
      this.dots = this.slides.map((slide, i) => {
        slide.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vh-slider__dot';
        dot.setAttribute('aria-label', `${label} ${i + 1}`);
        dot.addEventListener('click', () => {
          this.userInteracted();
          this.goTo(i);
        });
        wrap.appendChild(dot);
        return dot;
      });
    }

    goTo(i) {
      const n = this.slides.length;
      if (!n) return;
      const target = ((i % n) + n) % n;
      const slide = this.slides[target];
      this.track.scrollTo({left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth'});
    }

    step(dir) {
      this.userInteracted();
      if (this.mode === 'page') {
        this.track.scrollBy({left: dir * this.track.clientWidth * 0.9, behavior: reducedMotion ? 'auto' : 'smooth'});
      } else {
        this.goTo(this.index + dir);
      }
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      if (this.mode === 'slide') {
        this.index = Math.round(left / Math.max(1, this.track.clientWidth));
        this.dots?.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
        this.slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== this.index));
      } else {
        if (this.prevBtn) this.prevBtn.disabled = left <= 4;
        if (this.nextBtn) this.nextBtn.disabled = left >= max - 4;
      }
    }

    setupAutoplay() {
      if (this.dataset.autoplay !== 'true' || reducedMotion || this.slides.length < 2) return;
      this.interval = Math.max(3000, Number(this.dataset.interval) || 6000);
      this.paused = false;
      const pause = () => {
        this.paused = true;
      };
      const resume = () => {
        this.paused = false;
      };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.track.addEventListener('pointerdown', () => this.userInteracted(), {passive: true});
      this.track.addEventListener('touchstart', () => this.userInteracted(), {passive: true});
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.stop()));
      });
      this.visibilityObserver.observe(this);
    }

    play() {
      if (this.timer || this.stopped) return;
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        this.goTo(this.index + 1);
      }, this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    userInteracted() {
      this.stopped = true;
      this.stop();
    }
  }
  if (!customElements.get('vh-slider')) customElements.define('vh-slider', VhSlider);

  /* ------------------------------------------------------------------------
     <vh-tabs>: product tabs. Lazy panels are fetched through the Section
     Rendering API the first time they are opened.
     ------------------------------------------------------------------------ */
  class VhTabs extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.link = this.closest('section')?.querySelector('[data-vh-tabs-link]');
      this.limit = Number(this.dataset.limit) || 8;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = (i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.select(next);
        });
      });
      const first = this.querySelector('[role="tabpanel"]:not([hidden])');
      if (first?.hasAttribute('data-lazy')) this.load(first, this.tabs[0]);
    }

    select(i) {
      this.tabs.forEach((tab, j) => {
        const selected = i === j;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !selected;
        if (selected && panel.hasAttribute('data-lazy')) this.load(panel, tab);
      });
      if (this.link && this.tabs[i]?.dataset.url) this.link.href = this.tabs[i].dataset.url;
    }

    async load(panel, tab) {
      panel.removeAttribute('data-lazy');
      const base = (tab && (tab.dataset.fetchUrl || tab.dataset.url)) || '';
      if (!base) return;
      const url = `${base}${base.includes('?') ? '&' : '?'}section_id=vh-api-product-rail`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const html = (await response.text()).split('vh-api-product-rail-').join(`vh-api-${panel.dataset.block}-`);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rail = doc.querySelector('.vh-rail');
        if (!rail) throw new Error('empty');
        // the tab's own number of products ("Produkter i fanen"), else the section's
        const max = Number(panel.dataset.limit) || this.limit;
        rail.querySelectorAll('.vh-rail__track > .grid-item').forEach((item, index) => {
          if (index >= max) item.remove();
        });
        panel.innerHTML = '';
        panel.appendChild(document.importNode(rail, true));
        vh.updateWishlistButtons?.(panel);
      } catch (error) {
        panel.innerHTML = `<p class="vh-rail__empty"><a href="${base}">${(vh.config.strings && vh.config.strings.seeAll) || 'Se alle'}</a></p>`;
      }
    }
  }
  if (!customElements.get('vh-tabs')) customElements.define('vh-tabs', VhTabs);

  /* ------------------------------------------------------------------------
     <vh-lazy-rail>: a product rail fetched when it nears the screen, through
     the Section Rendering API (…?section_id=vh-api-product-rail). The current
     product is left out; the whole section hides when nothing is left. With a
     data-fallback-url (the wine's country after its region), an empty first
     answer tries that, and the heading and link follow (F-114).
     Used by sections/vh-pdp-rail.liquid ("Mere fra producenten").
     ------------------------------------------------------------------------ */
  class VhLazyRail extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const url = this.dataset.url;
      if (!url) return this.hideSection();
      if (!('IntersectionObserver' in window)) return this.load();
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.load();
        }
      }, {rootMargin: '600px 0px'});
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    hideSection() {
      const section = this.closest('[data-vh-lazy-section]') || this;
      section.hidden = true;
    }

    async load() {
      if (await this.fill(this.dataset.url)) return;
      const fallback = this.dataset.fallbackUrl;
      if (fallback && (await this.fill(fallback))) {
        const section = this.closest('[data-vh-lazy-section]');
        const title = section && section.querySelector('.vh-head__title');
        const link = section && section.querySelector('.vh-head__link');
        if (title && this.dataset.fallbackHeading) title.textContent = this.dataset.fallbackHeading;
        if (link) link.setAttribute('href', fallback);
        return;
      }
      this.hideSection();
    }

    /* The products at `base` in the row, the current product left out; how many there are */
    async fill(base) {
      const url = `${base}${base.includes('?') ? '&' : '?'}section_id=vh-api-product-rail`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rail = doc.querySelector('.vh-rail');
        if (!rail) return 0;
        const limit = Number(this.dataset.limit) || 8;
        const exclude = this.dataset.exclude;
        const items = Array.from(rail.querySelectorAll('.vh-rail__track > .grid-item'));
        let kept = 0;
        items.forEach((item) => {
          if ((exclude && item.dataset.productHandle === exclude) || kept >= limit) item.remove();
          else kept += 1;
        });
        if (!kept) return 0;
        this.innerHTML = '';
        this.appendChild(document.importNode(rail, true));
        if (typeof vh.updateWishlistButtons === 'function') vh.updateWishlistButtons(this);
        return kept;
      } catch (error) {
        return 0;
      }
    }
  }
  if (!customElements.get('vh-lazy-rail')) customElements.define('vh-lazy-rail', VhLazyRail);

  /* ------------------------------------------------------------------------
     Trustpilot TrustBox: load the bootstrap script only when a widget nears
     the viewport.
     ------------------------------------------------------------------------ */
  function initTrustboxes(root = document) {
    const boxes = root.querySelectorAll('[data-vh-trustbox]');
    if (!boxes.length) return;
    const load = (box) => {
      const widget = box.querySelector('.trustpilot-widget');
      if (!widget) return;
      if (window.Trustpilot) {
        window.Trustpilot.loadFromElement(widget, true);
        return;
      }
      if (document.querySelector('script[data-vh-tp]')) return;
      const inject = () => {
        if (document.querySelector('script[data-vh-tp]')) return;
        const script = document.createElement('script');
        script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
        script.async = true;
        script.dataset.vhTp = '';
        document.head.appendChild(script);
      };
      // A store whose scripts wait for Shopify's consent banner (Theme settings → Vinhuset: Tredjepart) loads the
      // TrustBox after a yes, as the chat (snippets/vh-third-party.liquid); everywhere else it loads straight away
      if (typeof window.vhWhenConsent === 'function') window.vhWhenConsent('preferences', inject);
      else inject();
    };
    if (!('IntersectionObserver' in window)) {
      boxes.forEach(load);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        load(entry.target);
      });
    }, {rootMargin: '400px 0px'});
    boxes.forEach((box) => observer.observe(box));
  }
  document.addEventListener('DOMContentLoaded', () => initTrustboxes());
  document.addEventListener('shopify:section:load', (e) => initTrustboxes(e.target));

  /* ------------------------------------------------------------------------
     Campaign countdowns (real end dates only) + sticky campaign CTA + code copy
     ------------------------------------------------------------------------ */
  function initCountdowns(root = document) {
    root.querySelectorAll('[data-vh-countdown]').forEach((el) => {
      if (el._vhCountdown) return;
      const end = vh.parseCphDate(el.dataset.end);
      if (!end) return;
      el._vhCountdown = true;
      const nums = {
        d: el.querySelector('[data-unit="d"]'),
        h: el.querySelector('[data-unit="h"]'),
        m: el.querySelector('[data-unit="m"]'),
        s: el.querySelector('[data-unit="s"]'),
      };
      const label = el.querySelector('[data-vh-countdown-label]');
      const tick = () => {
        const diff = end.getTime() - Date.now();
        if (diff <= 0) {
          el.classList.add('is-ended');
          if (label && el.dataset.endedText) label.textContent = el.dataset.endedText;
          clearInterval(timer);
          el.dispatchEvent(new CustomEvent('vh:countdown:ended', {bubbles: true}));
          return;
        }
        const total = Math.floor(diff / 1000);
        const parts = {d: Math.floor(total / 86400), h: Math.floor((total % 86400) / 3600), m: Math.floor((total % 3600) / 60), s: total % 60};
        Object.keys(parts).forEach((key) => {
          if (nums[key]) nums[key].textContent = String(parts[key]).padStart(2, '0');
        });
        el.hidden = false;
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }
  document.addEventListener('DOMContentLoaded', () => initCountdowns());
  document.addEventListener('shopify:section:load', (e) => initCountdowns(e.target));

  /* "Effekt på baggrunden" on a Tilbuds-slide (snippets/vh-slide-fx.liquid): the confetti and the doodles are drawn for
     the whole slide, but what the slide holds varies (Trustpilot, pictures, one or two products, a countdown). A piece
     that lands on the slide's own content is left out, worked out again whenever the slide or its content changes size,
     so the art never sits on the heading, a box, a price or the button (docs/QA.md, F-092). */
  function clearSlideFx(slide) {
    const boxes = [...slide.querySelectorAll('.vh-os__head > *, .vh-os__panel, .vh-os__label, .vh-os__prices, .vh-os__cta > *')]
      .filter((el) => !el.hidden)
      .map((el) => el.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0);
    const gap = 6;
    slide.querySelectorAll('.vh-os__fx svg').forEach((svg) => {
      // a group without a transform only sets the drawing style: each of its children is one piece (a whole doodle)
      const pieces = [...svg.children].flatMap((child) => (child.tagName.toLowerCase() === 'g' && !child.hasAttribute('transform') ? [...child.children] : [child]));
      pieces.forEach((piece) => {
        const rect = piece.getBoundingClientRect();
        // the drawing for the other screen size is not shown (no box); its pieces are checked when it is
        if (!rect.width && !rect.height) return;
        const hit = boxes.some((box) => rect.left < box.right + gap && rect.right > box.left - gap && rect.top < box.bottom + gap && rect.bottom > box.top - gap);
        piece.style.visibility = hit ? 'hidden' : '';
      });
    });
  }
  function initSlideFx(root = document) {
    root.querySelectorAll('.vh-os').forEach((slide) => {
      if (slide._vhFx || !slide.querySelector('.vh-os__fx')) return;
      slide._vhFx = true;
      let frame = 0;
      const run = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => clearSlideFx(slide));
      };
      // the slide's size (the screen), and its content: the countdown appears once its script runs, pictures arrive
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(run);
        [slide, ...slide.querySelectorAll('.vh-os__head, .vh-os__panel, .vh-os__cta')].forEach((el) => observer.observe(el));
      }
      run();
    });
  }
  document.addEventListener('DOMContentLoaded', () => initSlideFx());
  document.addEventListener('shopify:section:load', (e) => initSlideFx(e.target));

  function initStickyCta(root = document) {
    root.querySelectorAll('[data-vh-sticky-cta]').forEach((bar) => {
      if (bar._vhSticky) return;
      bar._vhSticky = true;
      const anchor = document.getElementById(bar.dataset.anchor);
      if (!anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(anchor);
    });
  }
  document.addEventListener('DOMContentLoaded', () => initStickyCta());
  document.addEventListener('shopify:section:load', (e) => initStickyCta(e.target));

  document.addEventListener('click', async (event) => {
    const code = event.target.closest('[data-vh-copy]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code.dataset.vhCopy);
      const original = code.textContent;
      code.textContent = (vh.config.strings && vh.config.strings.copied) || 'Kopieret!';
      setTimeout(() => {
        code.textContent = original;
      }, 1600);
    } catch (e) {}
  });
})();
